import type path from "path";
import type { z } from "../../../node_modules/zod/v4/classic/external.cjs";
import { defineFunctionCalls } from "../../tools/function_call.js";
import type { AgentMetadata } from "../browser/index.js";

/**
 * Loads both built-in and user-defined tools, with user tools overriding built-in ones.
 * @returns A map of function calls and a list of tool declarations for the config.
 */

export async function loadHybridTools({ workDir, tools }: AgentMetadata) {
  const allTools = new Map<string, Awaited<ReturnType<typeof defineFunctionCalls>> extends Map<any, infer V> ? V : never>();
  const toolDeclarations: Array<{ name: string; description: string; parameters: any; filepath: string; }> = [];

  // 1. Load built-in tools
  const builtInToolsDir = projectResolver("src/tools/coder");
  const builtInFc = await defineFunctionCalls(builtInToolsDir);
  for (const [key, value] of builtInFc) {
    allTools.set(key, value);
  }

  // 2. Load user-defined tools from workspace
  try {
    const userFc = await defineFunctionCalls(path.join(workDir, "tools"));
    for (const [key, value] of userFc) {
      allTools.set(key, value); // User tool overrides built-in
    }
  } catch (error) {
    // It's okay if the user's tool directory doesn't exist or has errors.
    if (error instanceof Error && "code" in error && error.code !== "ENOENT") {
      console.warn(`Could not load user-defined tools from ${workDir}:`, error);
    }
  }

  // 3. Generate final declarations with correct filepaths
  for (const [key, { module }] of allTools) {
    const { name, description, paramsSchema } = module;
    let parameters = {};
    if (paramsSchema) {
      try {
        parameters = z.toJSONSchema(z.object(paramsSchema));
      } catch (error) {
        console.warn(`Could not convert Zod schema for tool "${name}":`, error);
      }
    }

    const isBuiltIn = builtInFc.has(key) && builtInFc.get(key) === allTools.get(key);
    const filepath = isBuiltIn ? `<jixo_internal>/${key}` : path.resolve(workDir, "tools", `${key}.function_call.ts`);

    toolDeclarations.push({
      name: name ?? key,
      description: description || "",
      parameters,
      filepath,
    });
  }

  return { functionCallMap: allTools, toolDeclarations };
}
